import { compareFullName } from './helpers';

export const findHierarchyNode = (treeArray, nodeId, key = 'id') => {
  let result = null;

  if (!treeArray || !treeArray.length) return result;
  // nodes we should visit
  const queue = [...treeArray];

  while (queue.length) {
    const node = queue.shift();
    if (node[key] === nodeId) {
      result = node;
      break;
    }
    if (node.children) queue.push(...node.children);
  }

  return result;
};

export const findHierarchyParent = (treeArray, nodeId, key = 'id') => {
  let result = null;

  if (!treeArray || !treeArray.length) return result;

  const queue = [...treeArray];

  while (queue.length) {
    const node = queue.shift();
    if (node.children && node.children.length) {
      const ids = node.children.map((x) => x[key]);
      if (ids.includes(nodeId)) {
        result = node;
        break;
      }

      queue.push(...node.children);
    }
  }

  return result;
};

export const findAllParents = (treeArray, nodeId, key = 'id') => {
  let currentId = nodeId;
  const parents = [];

  if (!treeArray || !treeArray.length) return parents;

  const rootId = treeArray[0][key];

  while (currentId !== rootId) {
    const parentNode = findHierarchyParent(treeArray, currentId);
    parents.push(parentNode);
    currentId = parentNode[key];
  }

  return parents;
};

export function filterNodesByIds(
  treeArray = [],
  ids = [],
  included = true,
  key = 'id'
) {
  if (!treeArray.length) return [];

  let result = JSON.parse(JSON.stringify(treeArray));

  const filterData = (data, ids) =>
    data.filter((obj) => {
      if (obj.children) {
        obj.children = filterData(obj.children, ids);
      }
      let filtered = false;

      if (included) {
        filtered = ids.includes(obj[key]);
      } else {
        filtered = !!obj.children || (!obj.children && !ids.includes(obj[key]));
      }

      if (filtered && obj.children) obj.isDefaultExpanded = true;
      ids = ids.filter((x) => x !== obj[key]);
      return filtered;
    });

  return filterData(result, ids);
}

export function filterNodesAndUsersByIds(
  treeArray = [],
  ids = [],
  included = true,
  key = 'id'
) {
  if (!treeArray.length) return [];

  let result = JSON.parse(JSON.stringify(treeArray));

  const filterData = (data, ids) =>
    data.filter((obj) => {
      if (obj.users) {
        obj.children = [...(obj.children || []), ...obj.users];
      }
      if (obj.children) {
        obj.children = filterData(obj.children, ids);
      }
      let filtered = false;

      if (included) {
        filtered = ids.includes(obj[key]);
      } else {
        filtered = !!obj.children || (!obj.children && !ids.includes(obj[key]));
      }

      if (filtered && obj.children) obj.isDefaultExpanded = true;
      ids = ids.filter((x) => x !== obj[key]);
      return filtered;
    });

  return filterData(result, ids);
}

export const searchNodes = (
  treeArray = [],
  searchKey = '',
  labelKey = 'label'
) => {
  if (!treeArray.length) return [];

  let result = JSON.parse(JSON.stringify(treeArray));

  const filterData = (data) =>
    data.filter((obj) => {
      if (obj.children) obj.children = filterData(obj.children);
      return obj.children
        ? true
        : obj[labelKey].toLowerCase().includes(searchKey.toLowerCase());
    });

  return filterData(result);
};

export const mergeTree = (treeArray1 = [], treeArray2 = [], key = 'id') => {
  let result = null;
  // nodes we should visit
  const queue = JSON.parse(JSON.stringify(treeArray2));

  while (queue.length) {
    const node = queue.shift();
    if (node.children) queue.push(...node.children);
    const parent = findHierarchyParent(treeArray2, node[key]);
    if (!parent) continue;

    const found = findHierarchyNode(treeArray1, parent[key]);
    if (!found) continue;

    if (
      found.children &&
      !found.children.map((x) => x[key]).includes(node[key])
    ) {
      addNodeByParent(treeArray1, node, parent[key], true);
    }
  }
  return result;
};

export const addNodeByParent = (
  treeArray = [],
  newItem = null,
  parentKey = '',
  sortAfter = false, // sort option after adding new node (no sort as default)
  key = 'id'
) => {
  if (!newItem || !newItem[key]) return;

  // BFS - breath first search
  const traverse = (arr) => {
    arr.forEach((node) => {
      if (node[key] === parentKey) {
        node.children.push(newItem);
        if (sortAfter)
          node.children.sort((a, b) => {
            return compareFullName.call(null, a, b, true);
          });
        return;
      }
      if (node.children) traverse(node.children);
    });
  };

  return traverse(treeArray);
};

export const findRangeByKeys = (
  treeArray,
  startKey = '',
  endKey = '',
  key = 'id'
) => {
  let result = [];
  // DFS - deep first search
  const traverse = (arr) => {
    arr.forEach((node) => {
      if (!node.children) return;
      if (node.children) traverse(node.children);

      const keys = node.children.map((x) => x[key]);
      if (keys.includes(startKey)) {
        const startIdx = keys.indexOf(startKey);
        const endIdx = keys.indexOf(endKey);
        const first = startIdx < endIdx ? startIdx : endIdx;
        const last = startIdx < endIdx ? endIdx : startIdx;
        result.push(...node.children.slice(first, last + 1));
      }
    });
  };

  traverse(treeArray);

  return result;
};

export const getSelectedLeafNodes = (treeArray = [], ids = [], key = 'id') => {
  let result = [];

  if (!treeArray.length || !ids.length) return result;

  // BFS - breath first search
  const traverse = (arr) => {
    arr.forEach((node) => {
      if (node.children) traverse(node.children);
      else if (ids.includes(node[key])) result.push(node[key]);
    });
  };

  traverse(treeArray);

  return result;
};

export const getLeafNodes = (treeArray = [], key = 'id') => {
  let result = [];

  if (!treeArray.length) return result;

  // BFS - breath first search
  const traverse = (arr) => {
    arr.forEach((node) => {
      if (node.children) traverse(node.children);
      else {
        result.push(node[key]);
      }
    });
  };

  traverse(treeArray);

  return result;
};

export const setDisabledNodes = (
  treeArray = [],
  fn = (x) => {
    return x;
  },
  attr = 'isDisabled'
) => {
  if (!treeArray.length) return;

  const traverse = (tree) => {
    tree.forEach((node) => {
      if (!node.children) {
        node[attr] = typeof node.children === 'undefined' && !!fn(node);
      } else traverse(node.children);
    });
  };

  return traverse(treeArray);
};

export const findIndependentBranches = (
  treeArray = [],
  ids = [],
  isSearching = false,
  key = 'id'
) => {
  const result = [];

  if (!treeArray.length) return [];

  const traverse = (tree) => {
    tree.forEach((node) => {
      if (node.children && node.children.length) {
        traverse(node.children);

        const cidArray = node.children.map((x) => x[key]);
        // when all children selected and its not in search mode
        if (!isSearching && cidArray.every((i) => ids.includes(i))) {
          result.push(node[key]);
          return;
        } else {
          const unloadedBranchIds = node.children
            .filter((x) => x.children === null && ids.includes(x[key]))
            .map((x) => x[key]);
          if (unloadedBranchIds.length) {
            result.push(...unloadedBranchIds);
            return;
          }
        }
      }
    });
  };

  traverse(treeArray);

  return result;
};

export const replaceNodeById = (
  treeArray = [],
  newItem = null,
  id = '',
  key = 'id'
) => {
  if (!newItem || !newItem[key]) return;

  const traverse = (arr) => {
    arr.forEach((node) => {
      if (node[key] === id) {
        node.children = newItem.children;
      } else if (node.children) traverse(node.children);
    });
  };

  traverse(treeArray);

  return newItem.children.filter((x) => !x.isDisabled).map((x) => x[key]);
};

export const extractIds = (obj) => {
  const ids = [];
  const objCopy = JSON.parse(JSON.stringify(obj));

  const pushIds = (node) => {
    // This is set to true if any non-disabled user/leaf node is discovered
    let includeThisNode = false;
    if (node.users) {
      node.children = [...(node.children || []), ...node.users];
      delete node.users;
    }

    if (node.children && node.children.length > 0) {
      for (const childNode of node.children) {
        // eslint-disable-next-line no-prototype-builtins
        if (!childNode.hasOwnProperty('children')) {
          if (!childNode.isDisabled) {
            includeThisNode = true;
            ids.push(childNode.id);
          }
        } else {
          // If non-disabled users were already found on this parent node
          // then we don't care that there weren't any found on descendant nodes
          includeThisNode = pushIds(childNode) || includeThisNode;
        }
      }
    }
    if (includeThisNode) {
      ids.push(node.id);
    }
    return includeThisNode;
  };

  pushIds(objCopy);

  return ids;
};

export const cleanupTree = (treeArray = [], parentKeyName = 'parentId') => {
  if (!treeArray.length) return;

  const traverse = (tree) => {
    tree.forEach((node) => {
      if (node.children && node.children.length) {
        traverse(node.children);

        node.children = node.children.filter(
          (x) =>
            x.children === null ||
            (x.children && x.children.length) ||
            !(parentKeyName in x)
        );
      }
    });
  };

  return traverse(treeArray);
};

export const buildHierarchyMap = (groups, key = 'id') => {
  if (!groups) return {};

  const hierarchyMap = { [groups[key]]: groups };

  const traverse = (arr) => {
    arr.forEach((node) => {
      const groupId = node[key];
      hierarchyMap[groupId] = node;

      if (node.children && node.children.length) {
        traverse(node.children);
      }
    });
  };

  traverse(groups.children);

  return hierarchyMap;
};

export const getGroupHierarchyPath = (user, map = {}) => {
  let hierarchyPath = user;
  let currentGroup = map[user.groupId];

  while (currentGroup) {
    if (!hierarchyPath) {
      hierarchyPath = { ...currentGroup };
    } else {
      // Starts off with user we are recursively building a hierarchy for
      // and ends with a degenerate/pathological tree
      hierarchyPath = {
        ...currentGroup,
        children: [{ ...hierarchyPath }],
      };
    }

    if (currentGroup.parentId) {
      currentGroup = map[currentGroup.parentId];
    } else {
      currentGroup = null;
    }
  }

  return hierarchyPath;
};
